/**
 * OZK Display Name Utility
 *
 * OZK의 모든 활동은 닉네임만 사용합니다.
 * 이 함수는 다양한 필드 이름에서 닉네임을 추출하여 일관된 표시 이름을 반환합니다.
 */

interface UserLike {
  nickname_display?: string | null;
  nickname?: string | null;
  displayName?: string | null;
  display_name?: string | null;
  name?: string | null;
  authorNickname?: string | null; // 게시글 작성자 닉네임
  authorDisplayName?: string | null; // 게시글 작성자 표시 이름
  isAnonymous?: boolean | null;
  anonymous?: boolean | null;
  profile?: {
    nickname_display?: string | null;
    nickname?: string | null;
    displayName?: string | null;
  };
}

/**
 * 사용자 객체에서 표시 이름을 추출합니다.
 *
 * 우선순위:
 * 1. nickname_display (OZK 표준)
 * 2. nickname
 * 3. displayName / display_name
 * 4. name
 * 5. profile.nickname_display
 * 6. profile.nickname
 * 7. profile.displayName
 * 8. "익명" (fallback)
 *
 * @param userLike - 사용자 정보 객체 (멤버, 작성자 등)
 * @returns 표시할 이름 (기본값: "익명")
 */
export function getDisplayName(userLike: UserLike | null | undefined): string {
  if (!userLike) return "익명";

  // 익명 처리 플래그 확인
  const isAnonymous = userLike.isAnonymous ?? userLike.anonymous ?? false;

  if (isAnonymous) return "익명";

  // 우선순위에 따라 닉네임 추출
  // 게시글 작성자의 경우 authorNickname도 확인
  return (
    userLike.nickname_display ??
    userLike.nickname ??
    userLike.authorNickname ?? // 게시글 작성자 닉네임
    userLike.displayName ??
    userLike.display_name ??
    userLike.authorDisplayName ?? // 게시글 작성자 표시 이름
    userLike.name ??
    userLike.profile?.nickname_display ??
    userLike.profile?.nickname ??
    userLike.profile?.displayName ??
    "익명"
  );
}

/**
 * 사용자 객체에서 아바타 URL을 추출합니다.
 */
export function getAvatarUrl(
  userLike: UserLike | null | undefined
): string | null {
  if (!userLike) return null;

  return (
    (userLike as any).avatarUrl ??
    (userLike as any).avatar_url ??
    (userLike as any).profile?.avatarUrl ??
    (userLike as any).profile?.avatar_url ??
    null
  );
}
